import { Paper } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import { downloadExcel } from "react-export-table-to-excel";
import Select from "react-select";
import { upperCase } from "lodash";
import { baseUrl } from "../../../../services/config";

const FeeChallanSummaryReport = () => {
  const alert = useAlert();
  const authtoken = localStorage.getItem("userToken");
  const user = useSelector((state) => state.user.user);

  const [sessionData, setSessionData] = useState([]);
  const [academicSessionId, setAcademicSessionId] = useState("");
  const [financialSessionId, setFinancialSessionId] = useState("");
  const [campusData] = useState(user?.campuses || []);
  const [selectedCampuses, setSelectedCampuses] = useState([]);

  const [summary, setSummary] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // unit / logo for print header
  const [unitName, setUnitName] = useState("");
  const [icon, setIcon] = useState([]);

  // print
  const [printloading, setprintloading] = useState(false);
  const [text, setText] = useState("old boring text");
  const componentRef = useRef(null);
  const tableRef = useRef(null);
  const onBeforeGetContentResolve = useRef(null);

  const pageStyle = `
  @page {
    size: landscape !important ;
  }
  @media screen {
    #printSection {
      display: none;
    }
  }
  @media print {
    body * {
      visibility: hidden;
    }
    #printSection, #printSection * {
      visibility: visible;
    }
  }
  `;

  const campusOptions = campusData.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  useEffect(() => {
    getSettingData();
    sessions();
  }, []);

  const getSettingData = async () => {
    await fetch(`${baseUrl}/get-settings`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authtoken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setUnitName(upperCase(data.payload.settings.unit_name));
        setIcon(data.payload.settings.logo_file);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const sessions = () => {
    fetch(`${baseUrl}/session`, {
      headers: {
        Authorization: `Bearer ${authtoken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setSessionData(data.payload);
        data.payload.forEach((session) => {
          if (session.active_academic_year == "1") {
            setAcademicSessionId(session.id);
          }
          if (session.active_financial_year == "1") {
            setFinancialSessionId(session.id);
          }
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const formatAmount = (amount) => {
    const num = Number(amount || 0);
    return num.toLocaleString();
  };

  const handleGetData = () => {
    if (academicSessionId == "") {
      alert.error("Please select the academic session");
      return;
    }
    if (financialSessionId == "") {
      alert.error("Please select the financial session");
      return;
    }

    setLoading(true);
    setIsVisible(false);

    const data = {
      academic_session_id: academicSessionId,
      financial_session_id: financialSessionId,
    };
    if (selectedCampuses.length > 0) {
      data.campus_ids = selectedCampuses.map((c) => c.value);
    }

    fetch(`${baseUrl}/fee-challan-summary-report`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authtoken}`,
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setLoading(false);
        if (data.payload) {
          setSummary(data.payload.summary || null);
          setCampuses(data.payload.campuses || []);
          setMeta(data.payload);
          setIsVisible(true);
        } else {
          setSummary(null);
          setCampuses([]);
          setMeta(null);
          setIsVisible(true);
          alert.error(data.metadata?.message || "No data found");
        }
      })
      .catch((err) => {
        setLoading(false);
        setIsVisible(false);
        console.log("err is", err);
      });
  };

  const handleBeforePrint = React.useCallback(() => {
    console.log("`onBeforePrint` called");
  }, []);

  const handleOnBeforeGetContent = React.useCallback(() => {
    console.log("`onBeforeGetContent` called");
    setprintloading(true);
    setText("Loading new text...");

    return new Promise((resolve) => {
      onBeforeGetContentResolve.current = resolve;

      setTimeout(() => {
        setprintloading(false);
        setText("New, Updated Text!");
        resolve();
      }, 2000);
    });
  }, [setprintloading, setText]);

  const handleAfterPrint = React.useCallback(() => {
    console.log("`onAfterPrint` called");
  }, []);

  const summaryCards = [
    {
      title: "Total Challans",
      count: summary?.total_challans,
      amount: summary?.total_amount,
      color: "#3699FF",
    },
    {
      title: "Paid Challans",
      count: summary?.paid_challans,
      amount: summary?.paid_amount,
      color: "#1BC5BD",
    },
    {
      title: "Unpaid Challans",
      count: summary?.unpaid_challans,
      amount: summary?.unpaid_amount,
      color: "#F64E60",
    },
  ];

  // Report body, reused for the on-screen view and the hidden print section.
  // `attachTableRef` is only true for the on-screen copy so the Excel export
  // reads a single, stable table node.
  const reportBody = (attachTableRef) => (
    <div className="p-3">
      <div
        className="d-flex align-items-center mb-5"
        style={{ gap: "15px" }}
      >
        {icon && icon.length > 0 && (
          <img style={{ height: 60, width: 60 }} src={icon} alt="logo" />
        )}
        <div>
          <h3 className="mb-0">{unitName}</h3>
          <h5 className="text-muted">Fee Challan Summary Report</h5>
        </div>
      </div>

      {/* applied filters */}
      <div className="mb-4" style={{ fontSize: 14 }}>
        <div>
          <b>Academic Session:</b>{" "}
          {sessionData.find((s) => s.id == academicSessionId)?.year || "-"}
        </div>
        <div>
          <b>Financial Session:</b>{" "}
          {sessionData.find((s) => s.id == financialSessionId)?.year || "-"}
        </div>
        <div>
          <b>Financial Year:</b>{" "}
          {meta?.financial_year_start && meta?.financial_year_end
            ? `${meta.financial_year_start} to ${meta.financial_year_end}`
            : "-"}
        </div>
        <div>
          <b>Campuses:</b>{" "}
          {selectedCampuses.length > 0
            ? selectedCampuses.map((c) => c.label).join(", ")
            : "All Campuses"}
        </div>
      </div>

      {/* summary cards (inline styles so they render the same on screen and
          in the print iframe, where Bootstrap's responsive grid collapses) */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        {summaryCards.map((card) => (
          <div
            key={card.title}
            style={{
              flex: "1 1 0",
              padding: 16,
              borderRadius: 8,
              borderLeft: `5px solid ${card.color}`,
              background: "#F8F9FA",
            }}
          >
            <div style={{ color: "#7E8299", fontSize: 14, marginBottom: 4 }}>
              {card.title}
            </div>
            <div
              style={{ color: card.color, fontSize: 24, fontWeight: 700 }}
            >
              {formatAmount(card.count)}
            </div>
            <div style={{ color: "#3F4254" }}>
              Amount: {formatAmount(card.amount)}
            </div>
          </div>
        ))}
      </div>

      {/* per-campus breakdown */}
      <div id="tableboot" className="mt-4">
        <Table
          responsive={attachTableRef}
          bordered
          ref={attachTableRef ? tableRef : null}
        >
          <thead>
            <tr id="tbl">
              <th>Campus</th>
              <th>Total Challans</th>
              <th>Total Amount</th>
              <th>Paid Challans</th>
              <th>Paid Amount</th>
              <th>Unpaid Challans</th>
              <th>Unpaid Amount</th>
            </tr>
          </thead>
          <tbody>
            {campuses.length > 0 ? (
              <>
                {campuses.map((c) => (
                  <tr id="tbl" key={c.campus_id}>
                    <td style={{ color: "black" }}>{c.campus_name}</td>
                    <td style={{ color: "black" }}>
                      {formatAmount(c.total_challans)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(c.total_amount)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(c.paid_challans)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(c.paid_amount)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(c.unpaid_challans)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(c.unpaid_amount)}
                    </td>
                  </tr>
                ))}
                {summary && (
                  <tr
                    id="tbl"
                    style={{ fontWeight: "bold", background: "#F8F9FA" }}
                  >
                    <td style={{ color: "black" }}>Total</td>
                    <td style={{ color: "black" }}>
                      {formatAmount(summary.total_challans)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(summary.total_amount)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(summary.paid_challans)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(summary.paid_amount)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(summary.unpaid_challans)}
                    </td>
                    <td style={{ color: "black" }}>
                      {formatAmount(summary.unpaid_amount)}
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <tr>
                <td colSpan={7} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      <>
        <div className="row">
          <div className="col-md-3">
            <label>
              <b>Academic Session</b>
              <span style={{ color: "red", fontSize: 12 }}>*</span>
            </label>
            <select
              style={{ height: 40 }}
              className="form-select"
              value={academicSessionId}
              aria-label="Academic Session"
              onChange={(e) => setAcademicSessionId(e.target.value)}
            >
              <option value="">Select Session</option>
              {sessionData &&
                sessionData.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.year}
                  </option>
                ))}
            </select>
          </div>

          <div className="col-md-3">
            <label>
              <b>Financial Session</b>
              <span style={{ color: "red", fontSize: 12 }}>*</span>
            </label>
            <select
              style={{ height: 40 }}
              className="form-select"
              value={financialSessionId}
              aria-label="Financial Session"
              onChange={(e) => setFinancialSessionId(e.target.value)}
            >
              <option value="">Select Session</option>
              {sessionData &&
                sessionData.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.year}
                  </option>
                ))}
            </select>
          </div>

          <div className="col-md-3">
            <label>
              <b>Select Campuses</b>
            </label>
            <Select
              isMulti
              options={campusOptions}
              value={selectedCampuses}
              placeholder="All Campuses"
              onChange={(selected) => setSelectedCampuses(selected || [])}
            />
          </div>

          <div className="col-md-3 mt-9 d-flex align-items-start">
            <button
              className="btn btn-primary mr-4"
              style={{ height: 40 }}
              onClick={handleGetData}
            >
              Show
            </button>
            {isVisible && summary && (
              <div className="d-flex align-items-start">
                <ReactToPrint
                  documentTitle="Fee Challan Summary Report"
                  pageStyle={pageStyle}
                  onAfterPrint={handleAfterPrint}
                  onBeforeGetContent={handleOnBeforeGetContent}
                  onBeforePrint={handleBeforePrint}
                  removeAfterPrint={true}
                  trigger={() => (
                    <button
                      disabled={printloading ? true : false}
                      className="btn btn-primary w-100px mx-2"
                    >
                      {printloading ? <span>Printing..</span> : "Print"}
                    </button>
                  )}
                  content={() => componentRef.current}
                />
                <button
                  className="btn btn-primary mx-2"
                  onClick={() => {
                    if (!tableRef.current) {
                      alert.error("Please generate the report first");
                      return;
                    }
                    downloadExcel(
                      {
                        fileName: "Fee Challan Summary Report",
                        sheet: "Summary",
                      },
                      tableRef.current,
                    );
                  }}
                >
                  Export
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5">
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Spinner class="text-center" variant="info" animation="border" />
            </div>
          ) : (
            isVisible && summary && reportBody(true)
          )}
        </div>
      </>
      {isVisible && summary && (
        <div
          ref={componentRef}
          id="printSection"
          style={{ visibility: "hidden" }}
          className="tablePrint"
        >
          {reportBody(false)}
        </div>
      )}
    </Paper>
  );
};

export default FeeChallanSummaryReport;
